/**
 * Gestión de Plantillas — pantalla principal (§3.2 CORRECCIÓN 3).
 *
 * Muestra 8 tarjetas (una por tipo de documento). Al hacer click se navega a
 * `/admin/plantillas/tipo/:tipo` donde se administran únicamente las plantillas
 * de ese tipo (§3.3).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ReceiptIcon, ClipboardTextIcon, PackageIcon, TruckIcon, MoneyIcon,
  ArrowUUpLeftIcon, PlusMinusIcon, TableIcon, CheckCircleIcon, WarningCircleIcon,
  CaretRightIcon,
  CheckIcon, StarIcon
} from "@phosphor-icons/react";

const ICONS = {
  factura: ReceiptIcon,
  proforma: ClipboardTextIcon,
  orden_compra: PackageIcon,
  guia_remision: TruckIcon,
  recibo: MoneyIcon,
  nota_credito: ArrowUUpLeftIcon,
  nota_debito: PlusMinusIcon,
  estado_cuenta: TableIcon,
};

const DESCRIPTIONS = {
  factura: "Formato del comprobante fiscal principal con CAI, cliente, detalle e ISV.",
  proforma: "Cotización previa a la factura. Sin CAI, con vigencia.",
  orden_compra: "Emitida a proveedores. Detalle y totales.",
  guia_remision: "Documento de traslado de mercadería. Vinculado a la factura.",
  recibo: "Comprobante de pago recibido: desglose por factura, cancelada o abono parcial.",
  nota_credito: "Aplicada a factura o nota de débito. Descuenta o devuelve.",
  nota_debito: "Aplicada a factura. Agrega al saldo.",
  estado_cuenta: "Reporte con movimientos y saldo del cliente.",
};

export default function Templates() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/document-templates/summary-by-type");
      setTypes(data.types || []);
      setCanEdit(Boolean(data.puede_editar));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const seedDefaults = async () => {
    if (!window.confirm("Se volverá a crear el \"Formato del sistema\" de los tipos que no lo tengan. No se modifica ninguna plantilla existente. ¿Continuar?")) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/document-templates/seed-defaults");
      toast.success(`${data.total} plantilla${data.total !== 1 ? "s" : ""} creada${data.total !== 1 ? "s" : ""}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSeeding(false);
    }
  };

  const allEmpty = types.length > 0 && types.every((t) => t.total === 0);

  return (
    <Layout>
      <div className="p-6" data-testid="templates-home">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-heading font-bold text-text-primary">Gestión de Plantillas</h1>
            <p className="text-sm text-text-muted mt-1 max-w-2xl">
              Centro de administración visual de todos los documentos del sistema.
              Ingresa por tipo de documento para crear, editar, activar y definir la plantilla predeterminada.
            </p>
          </div>
          {canEdit && <button
            onClick={seedDefaults}
            disabled={seeding}
            data-testid="btn-seed-defaults"
            className={`text-xs rounded-md px-4 py-2 border font-medium transition-colors ${
              allEmpty
                ? "bg-agro-green text-black border-agro-green hover:bg-agro-green-hover"
                : "border-border text-text-secondary hover:border-agro-green hover:text-agro-green"
            }`}
          >
            {seeding ? "Creando…" : allEmpty ? <><StarIcon size={12} weight="fill" className="inline align-[-2px]" aria-hidden="true" /> Cargar plantillas iniciales</> : "Restaurar formatos del sistema"}
          </button>}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="bg-card border border-border/60 rounded-lg h-40 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {types.map((t) => {
              const Icon = ICONS[t.tipo] || ReceiptIcon;
              const hasDefault = Boolean(t.predeterminada);
              return (
                <button
                  key={t.tipo}
                  onClick={() => navigate(`/admin/plantillas/tipo/${t.tipo}`)}
                  data-testid={`tpl-card-${t.tipo}`}
                  className="group text-left bg-card border border-border/60 rounded-lg p-5 hover:border-agro-green/60 hover:shadow-lg hover:shadow-agro-green/5 transition-all flex flex-col justify-between min-h-[190px]"
                >
                  <div>
                    <div className="flex items-start justify-between">
                      <div className="w-11 h-11 rounded-md bg-agro-green/10 flex items-center justify-center group-hover:bg-agro-green/20 transition-colors">
                        <Icon size={22} weight="duotone" className="text-agro-green" />
                      </div>
                      <span className={`text-[10px] uppercase tracking-wider rounded px-2 py-0.5 ${
                        hasDefault
                          ? "bg-agro-green/15 text-agro-green"
                          : "bg-amber-500/10 text-amber-400"
                      }`}>
                        {hasDefault ? <><CheckIcon size={11} weight="bold" className="inline align-[-2px]" aria-hidden="true" /> Con predeterminada</> : "Sin predeterminada"}
                      </span>
                    </div>
                    <h3 className="text-base font-heading font-semibold mt-3">{t.label}</h3>
                    <p className="text-xs text-text-muted mt-1 line-clamp-2">
                      {DESCRIPTIONS[t.tipo]}
                    </p>
                  </div>
                  <div className="mt-3 pt-3 border-t border-border/40 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-3 text-text-muted">
                      <span data-testid={`tpl-total-${t.tipo}`}>{t.total} plantilla{t.total !== 1 ? "s" : ""}</span>
                      {t.activas > 0 && (
                        <span className="text-agro-green flex items-center gap-1">
                          <CheckCircleIcon size={11} weight="fill" />
                          {t.activas} activa{t.activas !== 1 ? "s" : ""}
                        </span>
                      )}
                      {t.total === 0 && (
                        <span className="text-amber-400 flex items-center gap-1">
                          <WarningCircleIcon size={11} weight="fill" />
                          Vacío
                        </span>
                      )}
                    </div>
                    <CaretRightIcon size={14} className="text-agro-green opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-8 p-4 rounded-md bg-card/40 border border-border/40 text-xs text-text-muted max-w-3xl">
          <strong className="text-white/80 font-semibold">¿Cómo funciona?</strong>
          <ul className="mt-2 space-y-1 list-disc list-inside">
            <li><span className="text-white/80">Predeterminada</span>: se usa automáticamente al imprimir/exportar ese tipo de documento.</li>
            <li><span className="text-white/80">Activa</span>: aparece en el selector <em>PLANTILLA</em> al emitir un documento (si hay más de una).</li>
            <li><span className="text-white/80">Inactiva</span>: se conserva para historial pero no se ofrece en el flujo operativo.</li>
            <li>Solo puede existir <em>una</em> plantilla predeterminada por tipo de documento y alcance (empresa o sucursal).</li>
            <li><span className="text-white/80">Formato único aprobado</span>: facturas, proformas, notas de crédito, notas de débito, órdenes de compra y estados de cuenta usan siempre el “Diseño institucional AGROPORT”; no admiten otras plantillas.</li>
            <li><span className="text-white/80">Formato del sistema</span>: el formato oficial de los demás documentos; al editarlo se publica una nueva versión y la anterior se conserva inactiva.</li>
            <li>Las plantillas usadas en documentos emitidos no se eliminan: se desactivan.</li>
          </ul>
        </div>
      </div>
    </Layout>
  );
}
